import clsx from "clsx";
import React, { useCallback, useEffect, useRef, useState } from "react";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { useAboveFold } from "web/react/hooks/use-above-fold/use-above-fold";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";

import {
    BrandAdContainerProps,
    BrandAdEvent,
    getPlacement,
    sendEvent,
} from "web/react/components/brand-ad-container/brand-ad-container";
import { gettext } from "web/script/modules/django-i18n";
import rothko from "web/script/modules/rothko";
import { useSendImpression } from "web/script/utils/ads";
import styles from "./brand-ad-container.module.css";

type InfoButtonType = "light" | "dark";

const STYLE_TYPE_CLASSES: { readonly [bt in InfoButtonType]: { icon: string; label: string } } = {
    light: { icon: styles.lightInfoIcon, label: styles.lightInfoLabel },
    dark: { icon: styles.darkInfoIcon, label: styles.darkInfoLabel },
};

interface InfoButtonProps {
    sendAnalyticsEvent: (action: BrandAdEvent["action"], area: string) => void;
    styleType: string;
    labelText: string;
}

function InfoButton({ sendAnalyticsEvent, styleType, labelText }: InfoButtonProps): JSX.Element {
    const [isActive, setIsActive] = useState(false);

    const iconClass = STYLE_TYPE_CLASSES[styleType]["icon"];
    const labelClass = STYLE_TYPE_CLASSES[styleType]["label"];
    if (!iconClass || !labelClass) {
        throw new Error(`Unknown type ${styleType}!`);
    }

    function toggleInfo(): void {
        sendAnalyticsEvent("click", "icon");
        setIsActive(!isActive);
    }

    return (
        <button
            className={clsx(styles.info, {
                [styles.active]: isActive,
            })}
            onClick={toggleInfo}
        >
            <span className={clsx(styles.infoIcon, iconClass)}>{"i"}</span>
            <span className={clsx(styles.infoLabel, labelClass)}>{labelText}</span>
        </button>
    );
}

const ENDPOINT = "modules/brand_ads";

export function BrandAdContainer({
    jpg_banner_image: jpgBannerImage,
    link,
    click_url: clickUrl,
    impression_url: impressionUrl,
    mobile_link: mobileLink,
    jpg_mpu_image: jpgMpuImage,
    product_image: productImage,
    product_link: productLink,
    product_description: productDescription,
    product_price: productPrice,
    partner,
    has_margin: hasMargin,
    retailer_network: retailerNetwork,
    campaign_id: campaignId,
    flight_id: flightId,
    creative_id: creativeId,
    ad_id: adId,
    internal: isInternalAd,
    placement_name: placementName,
    onOverlay = false,
}: BrandAdContainerProps): null | JSX.Element {
    const containerRef = useRef(null);
    const { isAboveFold, isVisible } = useAboveFold(containerRef);
    const [isLoaded, setIsLoaded] = useState(false);
    const { shouldSendImpression, setImpressionSent } = useSendImpression(isVisible, onOverlay);

    // as final insurance, don't accidentally send non-booleans to analytics customData

    const sendKevelTracking = useCallback(
        (isClick: boolean): void => {
            const type = "pba";
            const url = isClick ? clickUrl : impressionUrl;
            if (url) {
                const clicked = isClick ? "true" : "false";
                rothko.fetch(ENDPOINT, { url, clicked, type }, { cache: false });
            }
        },
        [clickUrl, impressionUrl]
    );

    const sendAnalyticsEvent = useCallback(
        (action: BrandAdEvent["action"], area: string, placementName?) => {
            const placement =
                containerRef.current && getPlacement(containerRef.current, placementName);
            sendEvent({
                action,
                area,
                retailerNetwork,
                campaignId,
                flightId,
                creativeId,
                isAboveFold,
                isInternalAd,
                adId,
                placement,
            });
        },
        [retailerNetwork, campaignId, flightId, creativeId, adId, isAboveFold, isInternalAd]
    );

    useEffect(() => {
        if (!isLoaded) {
            sendAnalyticsEvent("loaded", "no interaction", placementName);
            setIsLoaded(true);
        }
    }, [isLoaded, placementName, sendAnalyticsEvent]);

    useEffect(() => {
        if (shouldSendImpression) {
            sendKevelTracking(false);
            setImpressionSent(true);
            sendAnalyticsEvent("impression", "no interaction", placementName);
        }
    }, [
        placementName,
        sendKevelTracking,
        sendAnalyticsEvent,
        setImpressionSent,
        shouldSendImpression,
    ]);

    function onProductHeroClick(): void {
        sendKevelTracking(true);
        sendAnalyticsEvent("click", "product image", placementName);
    }

    function onMainButtonClick(): void {
        sendKevelTracking(true);
        sendAnalyticsEvent("click", "banner", placementName);
    }

    const partnerLabel = (
        <>
            <SVGIcon name="hyperlink" />
            <span className={styles.textWithIcon}>{partner}</span>
        </>
    );

    // key forces component to re-render on the client for the correct href.
    const { isDomLoaded, isMobileViewport } = useDomViewport();
    const mainButtonKey = isDomLoaded ? "client" : "server";
    const mainButtonHref = isMobileViewport ? link : mobileLink;

    const mainButton = (
        <a
            key={mainButtonKey}
            href={mainButtonHref}
            className="main-tracked-button"
            target="_blank"
            onClick={onMainButtonClick}
            rel="noreferrer"
        >
            {/* Note for future changes: If something regarding the media queries in images load or the picture sources change please update `base_page.jinja` as well since we are preloading them conditionally. */}
            <picture>
                <source media="(min-width: 576px)" srcSet={jpgBannerImage} />
                <source media="(max-width: 575px)" srcSet={jpgMpuImage} />
                <img className={styles.containerImage} src={jpgBannerImage} alt="test banner" />
            </picture>
            <div className={styles.bannerPartnerLabel}>{partnerLabel}</div>
        </a>
    );
    const featuredProductCard = (
        <div className={styles.partnerProductContainer}>
            <a
                className={styles.partnerProductLink}
                href={productLink}
                target="_blank"
                onClick={onProductHeroClick}
                rel="noreferrer"
            >
                <img
                    className={clsx(styles.containerImage, styles.partnerProductImage)}
                    src={productImage}
                    alt={productDescription ?? undefined}
                />
                <div className={styles.productDetailsContainer}>
                    <div className={styles.productDetails}>{productDescription}</div>
                    {productPrice && (
                        <div className={styles.productPrice}>
                            <SVGIcon name="truck" />
                            <span className={styles.textWithIcon}>{productPrice}</span>
                        </div>
                    )}
                    <div className={styles.productPartnerLabel}>{partnerLabel}</div>
                </div>
            </a>
            <InfoButton
                sendAnalyticsEvent={sendAnalyticsEvent}
                styleType="light"
                labelText={gettext("feed.header.featured_label")} // Featured
            />
        </div>
    );
    return (
        <div
            ref={containerRef}
            className={clsx(styles.container, {
                [styles.hasMargin]: hasMargin,
            })}
        >
            {featuredProductCard}
            <div className={styles.bannerContainer}>
                {mainButton}
                <InfoButton
                    sendAnalyticsEvent={sendAnalyticsEvent}
                    styleType="dark"
                    labelText={gettext("feed.header.sponsored_label")} // Sponsored
                />
            </div>
        </div>
    );
}
